/**
 * One readable line per intel insight — shared by the conversation cards (entity.intel-source skill)
 * and the unified intel-source screen, so an insight reads the same everywhere and no surface keeps
 * its own copy. Pure; no module logic (every kind of the shared IntelInsightPayload union).
 */
import type { InsightLevel, IntelInsightPayload } from '../db/schema/intel-source-insights.js';

export function clipText(text: string, max: number): string {
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length <= max ? flat : `${flat.slice(0, max - 1)}…`;
}

export const INSIGHT_KIND_LABELS: Record<IntelInsightPayload['kind'], string> = {
  market_view: 'Market view',
  strategy_description: 'Strategy',
  indicator_description: 'Indicator',
  trade_setup: 'Trade setup',
  risk_rule: 'Risk rule',
  ticker_mention: 'Mentioned',
  recommendation_claim: 'Claim',
  performance_claim: 'Performance claim',
  source_reference: 'Refers to',
  research_claim: 'Research claim',
  regulatory_action: 'Regulatory action',
  price_level: 'Level',
  principle: 'Principle',
  call: 'Call',
};

/** `60,000`, `-3.5%`, `57,000 (daily close)` — a level exactly as the source stated it. */
export function formatInsightLevel(level: InsightLevel): string {
  const value =
    level.unit === 'percent' ? `${level.value}%` : level.value.toLocaleString('en-US', { maximumFractionDigits: 8 });
  return level.note ? `${value} (${clipText(level.note, 60)})` : value;
}

type TradeSetupPayload = Extract<IntelInsightPayload, { kind: 'trade_setup' }>;

/** What a trade setup is about: its symbol, else its first instrument, else its name. */
export function tradeSetupSubject(payload: TradeSetupPayload): string {
  return payload.symbol ?? payload.instruments?.[0] ?? payload.setupName ?? 'Setup';
}

/**
 * The levels of a trade setup as one line — entry, stop, targets, invalidation, horizon — falling back to the
 * source's own words for the stop and target when it named no number.
 */
export function tradeSetupLevels(payload: TradeSetupPayload): string {
  const levels = payload.levels;
  const parts: string[] = [];
  if (levels?.entry) parts.push(`entry ${formatInsightLevel(levels.entry)}`);
  if (levels?.stop) parts.push(`stop ${formatInsightLevel(levels.stop)}`);
  else if (payload.stopApproach) parts.push(`stop: ${clipText(payload.stopApproach, 80)}`);
  if (levels?.targets && levels.targets.length > 0) {
    parts.push(`targets ${levels.targets.map(formatInsightLevel).join(' / ')}`);
  } else if (payload.targetApproach) parts.push(`target: ${clipText(payload.targetApproach, 80)}`);
  if (levels?.invalidation) parts.push(`invalidation ${formatInsightLevel(levels.invalidation)}`);
  if (payload.timeframe) parts.push(payload.timeframe);
  return parts.length > 0 ? parts.join(' · ') : 'no levels stated';
}

type PrincipleInsightPayload = Extract<IntelInsightPayload, { kind: 'principle' }>;

/**
 * How a principle relates its entities, as one phrase: `DXY ↔ Gold (inverse)`, `Fed cuts → Gold (leads 30d)` — the
 * members as said; null when it names no relation.
 */
export function principleRelationPhrase(payload: PrincipleInsightPayload): string | null {
  const relation = payload.relation;
  if (!relation || relation.members.length < 2) return null;
  const names = relation.members.map((member) => member.mention);
  const joined = relation.verb === 'co_moves' ? names.join(' ↔ ') : names.join(' → ');
  const detail = [
    relation.verb === 'co_moves' ? null : relation.verb,
    relation.sign ?? null,
    relation.lagDays ? `${relation.lagDays}d` : null,
  ].filter((part): part is string => part !== null);
  return detail.length > 0 ? `${joined} (${detail.join(' ')})` : joined;
}

/** `{ label, value }` for an insight — the claim, quoted where it came from the speaker. */
export function insightLine(payload: IntelInsightPayload): { label: string; value: string } {
  const label = INSIGHT_KIND_LABELS[payload.kind] ?? payload.kind;
  const quote = 'quote' in payload && payload.quote ? ` — “${clipText(payload.quote, 160)}”` : '';
  const value = (() => {
    switch (payload.kind) {
      case 'market_view':
        return `${payload.symbol ?? 'Market'} ${payload.direction} (${payload.timeframe})${quote}`;
      case 'strategy_description':
        return `${payload.name ? `${payload.name}: ` : ''}${payload.summary}${quote}`;
      case 'indicator_description':
        return `${payload.name}: ${payload.summary}`;
      case 'trade_setup':
        return `${tradeSetupSubject(payload)}${payload.side ? ` ${payload.side}` : ''}: ${tradeSetupLevels(payload)}${quote}`;
      case 'price_level':
        return `${payload.symbol} ${payload.role} ${formatInsightLevel(payload.level)}${payload.timeframe ? ` (${payload.timeframe})` : ''}${quote}`;
      case 'risk_rule':
        return payload.description;
      case 'ticker_mention':
        return `${payload.symbol} ${payload.stance}${quote}`;
      case 'recommendation_claim':
        return `${payload.action}${quote}`;
      case 'performance_claim':
        return `${payload.metric} ${payload.value}${payload.period ? ` (${payload.period})` : ''}`;
      case 'source_reference':
        return `${payload.platform} ${payload.identifier}`;
      case 'research_claim':
        return payload.statement;
      case 'regulatory_action':
        return `${payload.authority} ${payload.action}: ${payload.summary}${quote}`;
      case 'principle': {
        const relation = principleRelationPhrase(payload);
        const conditions = (payload.conditions ?? []).map((condition) => condition.text).join('; ');
        const basis = payload.basis === 'pattern' ? ' [read across its items]' : '';
        const retracted = payload.retracted ? ' [retracted]' : '';
        return `${relation ? `${relation}: ` : ''}${payload.statement}${conditions ? ` (when ${conditions})` : ''}${basis}${retracted}${quote}`;
      }
      case 'call': {
        const direction = payload.claim.direction ? ` ${payload.claim.direction}` : '';
        const horizon = payload.horizon.stated ? ` (${payload.horizon.label})` : '';
        return `${payload.callType}: ${payload.subject.mention}${direction} — ${payload.claim.label ?? ''}${horizon}${quote}`;
      }
    }
  })();
  return { label, value: clipText(value, 500) };
}

/** `[hh:mm:ss]` of a second offset. */
export function formatMoment(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  return [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60].map((n) => String(n).padStart(2, '0')).join(':');
}

/** A deep link to a moment on the item's own URL (`t=` — honored by platforms that support moments). */
export function momentUrl(itemUrl: string | null | undefined, seconds: number): string | null {
  if (!itemUrl) return null;
  return `${itemUrl}${itemUrl.includes('?') ? '&' : '?'}t=${Math.max(0, Math.floor(seconds))}s`;
}
